(function () {
  const openCartDrawerFromRedirect = () => {
    const url = new URL(window.location.href);
    const shouldOpenCartDrawer =
      url.searchParams.get('open_cart_drawer') === 'true' || sessionStorage.getItem('openCartDrawer') === 'true';

    if (!shouldOpenCartDrawer) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

    sessionStorage.removeItem('openCartDrawer');
    url.searchParams.delete('open_cart_drawer');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    cartDrawer.open();
  };

  document.addEventListener('DOMContentLoaded', () => {
    requestAnimationFrame(openCartDrawerFromRedirect);
  });

  window.addEventListener('pageshow', () => {
    requestAnimationFrame(openCartDrawerFromRedirect);
  });
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

if (!customElements.get('announcement-bar-timer')) {
  class AnnouncementBarTimer extends HTMLElement {
    static get observedAttributes() { return ["end-date"]; }

    connectedCallback() {
      console.log('START')
      this.hoursEl = this.querySelector("[data-hours]");
      this.minutesEl = this.querySelector("[data-minutes]");
      this.secondsEl = this.querySelector("[data-seconds]");

      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    attributeChangedCallback() {
      if (this.isConnected) this.start();
    }

    start() {
      this.stop();

      const endStr = this.getAttribute("end-date");
      const end = new Date(endStr);

      if (!endStr || Number.isNaN(end.getTime())) return;

      this.endTime = end.getTime();

      const tick = () => {
        const diff = this.endTime - Date.now();

        if (diff <= 0) {
          this.render(0);
          this.stop();
          this.dispatchEvent(new CustomEvent("timer:ended", { bubbles: true }));
          return;
        }

        this.render(diff);
      };

      tick();
      this._interval = setInterval(tick, 1000);
    }

    stop() {
      if (this._interval) clearInterval(this._interval);
      this._interval = null;
    }

    render(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, "0");
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, "0");
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, "0");
    }
  }
  customElements.define("announcement-bar-timer", AnnouncementBarTimer);
}

class KlaviyoCountdownManager extends EventTarget {
  constructor() {
    super();
    this.storageKey = 'celvana:klaviyo-form-submissions';
    this.handleKlaviyoForm = this.handleKlaviyoForm.bind(this);
    window.addEventListener('klaviyoForms', this.handleKlaviyoForm);
  }

  getSubmissions() {
    try {
      const submissions = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      return submissions && typeof submissions === 'object' && !Array.isArray(submissions) ? submissions : {};
    } catch (error) {
      return {};
    }
  }

  saveSubmissions(submissions) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(submissions));
      return true;
    } catch (error) {
      return false;
    }
  }

  handleKlaviyoForm(event) {
    const formId = String(event.detail?.formId || '').trim();
    if (event.detail?.type !== 'submit' || !formId) return;

    const submissions = this.getSubmissions();
    const existingSubmission = submissions[formId];
    const existingSubmittedAt = Number(existingSubmission?.submittedAt);
    const hasExistingSubmission = Number.isFinite(existingSubmittedAt) && existingSubmittedAt > 0;
    const submittedAt = hasExistingSubmission ? existingSubmittedAt : Date.now();

    if (!hasExistingSubmission) {
      submissions[formId] = {
        submittedAt,
        status: 'active',
      };
      this.saveSubmissions(submissions);
    }

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { formId, submittedAt },
      })
    );
  }

  expire(formId) {
    if (!formId) return;

    const submissions = this.getSubmissions();
    const submission = submissions[formId];
    if (!submission || submission.status === 'expired') return;

    submission.status = 'expired';
    submissions[formId] = submission;
    this.saveSubmissions(submissions);
  }
}

const klaviyoCountdownManager = new KlaviyoCountdownManager();

if (!customElements.get('product-countdown-timer')) {
  class ProductCountdownTimer extends HTMLElement {
    connectedCallback() {
      this.stop();

      this.daysElement = this.querySelector('[data-countdown-days]');
      this.hoursElement = this.querySelector('[data-countdown-hours]');
      this.minutesElement = this.querySelector('[data-countdown-minutes]');
      this.secondsElement = this.querySelector('[data-countdown-seconds]');

      if (this.hasAttribute('klaviyo-trigger')) {
        const durationHours = Number(this.getAttribute('duration-hours'));
        this.duration = (Number.isFinite(durationHours) && durationHours > 0 ? durationHours : 72) * 60 * 60 * 1000;
        this.formIds = this.getAttribute('klaviyo-form-ids')
          ?.split(/[\s,]+/)
          .map((formId) => formId.trim())
          .filter(Boolean);
        this.handleKlaviyoSubmission = (event) => {
          if (!this.formIds?.includes(event.detail?.formId)) return;
          this.initializeKlaviyoTimer(event.detail);
        };
        klaviyoCountdownManager.addEventListener('change', this.handleKlaviyoSubmission);
        this.initializeKlaviyoTimer();
        return;
      }

      const endDate = this.getAttribute('end-date')?.trim();
      const normalizedEndDate = /^\d{4}-\d{2}-\d{2}$/.test(endDate) ? `${endDate}T23:59:59` : endDate;
      this.start(new Date(normalizedEndDate).getTime());
    }

    disconnectedCallback() {
      this.stop();
      klaviyoCountdownManager.removeEventListener('change', this.handleKlaviyoSubmission);
    }

    initializeKlaviyoTimer(fallbackSubmission) {
      this.stop();

      if (!this.formIds?.length) {
        this.hidden = true;
        return;
      }

      const submissions = klaviyoCountdownManager.getSubmissions();
      const matchingSubmissions = this.formIds
        .map((formId) => {
          const submission = submissions[formId];
          if (!submission) return null;

          return {
            formId,
            submittedAt: Number(submission.submittedAt),
            status: submission.status,
          };
        })
        .filter((submission) => Number.isFinite(submission?.submittedAt))
        .sort((first, second) => first.submittedAt - second.submittedAt);

      let submission = matchingSubmissions[0];
      if (!submission && this.formIds.includes(fallbackSubmission?.formId)) {
        submission = fallbackSubmission;
      }

      if (!submission) {
        this.hidden = true;
        return;
      }

      const expiresAt = Number(submission.submittedAt) + this.duration;
      this.activeKlaviyoFormId = submission.formId;

      if (submission.status === 'expired' || expiresAt <= Date.now()) {
        this.expireKlaviyoTimer();
        return;
      }

      this.start(expiresAt);
    }

    start(endTime) {
      this.endTime = endTime;

      if (Number.isNaN(this.endTime)) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.update();
      if (!this.hidden) {
        this.interval = window.setInterval(() => this.update(), 1000);
      }
    }

    stop() {
      window.clearInterval(this.interval);
      this.interval = null;
    }

    update() {
      const remainingSeconds = Math.max(0, Math.ceil((this.endTime - Date.now()) / 1000));
      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;

      this.daysElement.textContent = String(days).padStart(2, '0');
      this.hoursElement.textContent = String(hours).padStart(2, '0');
      this.minutesElement.textContent = String(minutes).padStart(2, '0');
      this.secondsElement.textContent = String(seconds).padStart(2, '0');

      if (remainingSeconds === 0) {
        this.stop();
        this.hidden = true;
        this.expireKlaviyoTimer();
        this.dispatchEvent(new CustomEvent('timer:ended', { bubbles: true }));
      }
    }

    expireKlaviyoTimer() {
      this.hidden = true;
      if (!this.activeKlaviyoFormId) return;
      klaviyoCountdownManager.expire(this.activeKlaviyoFormId);
    }
  }

  customElements.define('product-countdown-timer', ProductCountdownTimer);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (!this.classList.contains('media-modal')) {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    this.dispatchEvent(new CustomEvent('modalOpened'));
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class ScrollCards extends HTMLElement {
  constructor() {
    super();

    this.move = this.moveBar.bind(this);
  }

  connectedCallback() {
    this.scrollBox = this.querySelector('[data-scroll-box]');
    this.scrollTrack = this.querySelector('[data-scroll-track]');
    this.scrollThumb = this.querySelector('[data-scroll-thumb]');
    if (!this.scrollBox || !this.scrollThumb || !this.scrollTrack) return;
    this.isScrolling = false;

    this.spaceScroll = this.scrollBox.scrollWidth - this.scrollBox.clientWidth;
    this.spaceTrack = this.scrollTrack.clientWidth - this.scrollThumb.clientWidth;

    this.scrollBox.addEventListener('scroll', this.move);
  }

  disconnectedCallback() {
    this.scrollBox?.removeEventListener('scroll', this.move);
  }

  moveBar() {
    if (!this.isScrolling) {
      window.requestAnimationFrame(() => {
        const multiplier = this.spaceTrack / this.spaceScroll;
        const position = this.scrollBox.scrollLeft;
        const distance = position * multiplier;

        this.scrollThumb.style.transform = `translateX(${distance}px)`;

        this.isScrolling = false;
      });
      this.isScrolling = true;
    }
  }
}

if (!customElements.get('scroll-cards')) {
  customElements.define('scroll-cards', ScrollCards);
}

class SwiperConfigurator {
  static swiperInstances = new Map();
  static swiperFilesPromise = null;

  constructor(sliderElement) {
    this.element = sliderElement;
    this.options = this.parseConfig();
    this.setupEventListeners();

    if (this.options.observer) {
      this.initIntersectionObserver();
    } else {
      this.initializeSwiper();
    }
  }

  parseConfig() {
    const { dataset: { config, mobile, observer } } = this.element;

    try {
      return {
        config: JSON.parse(config),
        mobile: mobile === "true",
        observer: observer === "true"
      };
    } catch (error) {
      console.error("Error parsing slider configuration:", error);
      return { config: {}, mobile: false, observer: false };
    }
  }

  initializeSwiper() {
    if (this.shouldDisableSwiper()) {
      return;
    }
    if (!Swiper || !this.element || this.element.swiper) {
      return;
    }
    if (this.options.config.thumbs?.swiper) {
      const thumbsSelector = this.options.config.thumbs.swiper;
      const thumbsElement = document.querySelector(thumbsSelector);
      const thumbsInstance = SwiperConfigurator.swiperInstances.get(thumbsElement);

      if (!thumbsInstance) {
        setTimeout(() => this.initializeSwiper(), 0);
        return;
      }
      this.options.config.thumbs.swiper = thumbsInstance;
    }

    this.addCustomPagination();

    const instance = new Swiper(this.element, this.options.config);
    SwiperConfigurator.swiperInstances.set(this.element, instance)
  }

  addCustomPagination() {
    if (!this.element.hasAttribute('data-custom-pagination')) return;

    const slides = Array.from(this.element.querySelectorAll('.swiper-slide'));

    this.options.config.pagination = this.options.config.pagination;

    this.options.config.pagination.renderBullet = (index, className) => {
      const label = slides[index].dataset.paginationLabel;
      return `<button class="${className}" type="button">${label}</button>`;
    }
  }

  shouldDisableSwiper() {
    return this.options.mobile && window.innerWidth > 768;
  }

  setupEventListeners() {
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  handleResize() {
    if (this.shouldDisableSwiper()) {
      this.destroySwiper();
    } else {
      this.initializeSwiper();
    }
  }

  destroySwiper() {
    if (this.element && this.element.swiper) {
      this.element.swiper.destroy(true, true);
    }
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('Element is in view, initializing swiper');
          this.observer.disconnect();
          this.initializeSwiper();
        }
      });
    }, { threshold: 0.1 });

    this.observer.observe(this.element);
  }

  static appendTagToBody(tagType, filePath) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement(tagType === 'script' ? 'script' : 'link');

      if (tagType === 'script') {
        tag.src = filePath;
        tag.type = 'text/javascript';
      } else if (tagType === 'style') {
        tag.href = filePath;
        tag.rel = 'stylesheet';
      } else {
        return reject(new Error('Unsupported tag type. Please use "script" or "style".'));
      }

      tag.onload = resolve;
      tag.onerror = () => reject(new Error(`Failed to load ${tagType} file: ${filePath}`));

      document.body.appendChild(tag);
    });
  }

  static appendSwiperFiles() {
    if (SwiperConfigurator.swiperFilesPromise) {
      return SwiperConfigurator.swiperFilesPromise;
    }
    const { css, js } = window.swiperFile || {};
    const files = [
      { type: 'style', path: css },
      { type: 'script', path: js },
    ].filter(file => file.path);

    if (files.length === 0) {
      SwiperConfigurator.swiperFilesPromise = Promise.resolve();
      return SwiperConfigurator.swiperFilesPromise;
    }
    const loadFilePromises = files.map(file => SwiperConfigurator.appendTagToBody(file.type, file.path));
    SwiperConfigurator.swiperFilesPromise = Promise.all(loadFilePromises);
    return SwiperConfigurator.swiperFilesPromise;
  }
}

const initSwipers = (root = document) => {
  const sliderElements = root.querySelectorAll("[data-slider-selector]");

  if (sliderElements.length > 0) {
    SwiperConfigurator.appendSwiperFiles()
      .then(() => {
        const sliders = Array.from(sliderElements)
          .filter(slider => !slider.hasAttribute('data-slider-ingredients'));
        const hasThumbs = (slider) =>
          typeof slider.dataset.config === 'string' &&
          slider.dataset.config.includes('"thumbs"');

        sliders
          .sort((a, b) => Number(hasThumbs(a)) - Number(hasThumbs(b)))
          .forEach((slider) => {
            if (slider.swiper || slider.dataset.swiperConfigured === 'true') return;
            slider.dataset.swiperConfigured = 'true';
            new SwiperConfigurator(slider);
          });
      }).catch((error) => {
        console.error('Failed to load Swiper files:', error);
      });
  }
};

document.addEventListener('DOMContentLoaded', () => {
  initSwipers();
});

document.addEventListener('shopify:section:load', (event) => {
  initSwipers(event.target);
});

document.addEventListener('cart:rendered', (event) => {
  initSwipers(event.target);
});

if (!customElements.get('video-player')) {
  customElements.define('video-player', class VideoPlayer extends HTMLElement {
    constructor() {
      super();
      this.handleTogglePlayback = this.handleTogglePlayback.bind(this);
      this.handleExternalVideoButtonClick = this.handleExternalVideoButtonClick.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleVideoPause = this.handleVideoPause.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
      this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
      this.handleWebkitEndFullscreen = this.handleWebkitEndFullscreen.bind(this);
      this.isMobileFullScreen = this.dataset.mobileFullscreen === 'true';
    }

    connectedCallback() {
      this.videoElement = this.querySelector('[data-video]');
      this.playButton = this.querySelector('[data-play-button]');
      this.clickOverlay = this.querySelector('[data-overlay]');
      this.externalVideoButton = this.closest('.media-list')?.querySelector('[data-video-button]') || null;

      if (!this.videoElement) return;

      this.initPlaybackControls();
      this.initFullscreenHandlers();
      this.initStateListeners();
    }

    disconnectedCallback() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      this.externalVideoButton?.removeEventListener('click', this.handleExternalVideoButtonClick);
    }

    initPlaybackControls() {
      this.playButton?.addEventListener('click', this.handleTogglePlayback);
      this.clickOverlay?.addEventListener('click', this.handleTogglePlayback);
      this.externalVideoButton?.addEventListener('click', this.handleExternalVideoButtonClick);
    }

    initStateListeners() {
      this.videoElement?.addEventListener('play', this.handleVideoPlay);
      this.videoElement?.addEventListener('pause', this.handleVideoPause);
      this.videoElement?.addEventListener('ended', this.handleVideoEnded);
    }

    initFullscreenHandlers() {
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      this.videoElement?.addEventListener('webkitendfullscreen', this.handleWebkitEndFullscreen);
    }

    handleTogglePlayback(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!this.videoElement) return;

      if (this.videoElement.paused) {
        this.playVideo({ requestFullscreenOnMobile: true });
      } else {
        this.pauseVideo();
      }
    }

    handleExternalVideoButtonClick(event) {
      event.preventDefault();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.handleTogglePlayback();
    }

    playVideo(options = {}) {
      const { requestFullscreenOnMobile = false } = options;

      if (!this.videoElement || !this.videoElement.paused) return;

      const playPromise = this.videoElement.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (requestFullscreenOnMobile && this.checkIsMobileDevice() && this.isMobileFullScreen) {
              this.requestVideoFullscreen();
            }
          })
          .catch(error => {
            console.warn("Playback prevented:", error);
          });
      }
    }

    pauseVideo() {
      this.videoElement?.pause();
    }

    handleVideoPlay() {
      this.classList.add('is-playing');
    }

    handleVideoPause() {
      this.classList.remove('is-playing');
    }

    handleVideoEnded() {
      this.handleVideoPause();
    }

    handleFullscreenChange() {
      const isFullscreenActive = document.fullscreenElement || document.webkitFullscreenElement;

      if (!isFullscreenActive && this.videoElement && !this.videoElement.paused) {
        this.videoElement.pause();
      }
    }

    handleWebkitEndFullscreen() {
      this.videoElement?.pause();
    }

    requestVideoFullscreen() {
      const video = this.videoElement;
      if (!video) return;

      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    }

    checkIsMobileDevice() {
      return window.innerWidth <= 768;
    }
  });
}

if (!customElements.get('modal-video-autoplay')) {
  customElements.define('modal-video-autoplay', class ModalVideoAutoplay extends HTMLElement {
    constructor() {
      super();

      this.handleModalOpened = this.handleModalOpened.bind(this);
      this.handleModalClosed = this.handleModalClosed.bind(this);
    }

    connectedCallback() {
      this.modalElement = this.closest('modal-dialog');
      this.playerElement = this.querySelector('video-player[data-autoplay-on-modal-open="true"]');

      if (!this.modalElement && !this.playerElement) return;

      this.modalElement.addEventListener('modalOpened', this.handleModalOpened);
      document.body.addEventListener('modalClosed', this.handleModalClosed);
    }

    disconnectedCallback() {
      this.modalElement?.removeEventListener('modalOpened', this.handleModalOpened);
      document.body.removeEventListener('modalClosed', this.handleModalClosed);
    }

    handleModalOpened() {
      this.playerElement.playVideo?.();
    }

    handleModalClosed() {
      this.playerElement.pauseVideo?.();
    }
  });
}

if (!customElements.get('community-grid')) {
  class CommunityGrid extends HTMLElement {
    constructor() {
      super();
      this.playingClass = 'community-grid__media--playing';
      this.hiddenItemClass = 'community-grid__item--hidden';

      this.handleItemsClick = this.handleItemsClick.bind(this);
      this.handleLoadMore = this.handleLoadMore.bind(this);
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.itemsContainer = this.querySelector('[data-grid-items]');
        this.loadMoreBtn = this.querySelector('[data-load-more]');

        if (this.itemsContainer) {
          this.itemsContainer.addEventListener('click', this.handleItemsClick);
        }

        this.loadMoreBtn?.addEventListener('click', this.handleLoadMore);
      });
    }

    disconnectedCallback() {
      this.itemsContainer?.removeEventListener('click', this.handleItemsClick);
      this.loadMoreBtn?.removeEventListener('click', this.handleLoadMore);
    }

    handleLoadMore() {
      const hiddenItems = this.querySelectorAll('[data-hidden-item]');

      hiddenItems.forEach(item => {
        item.classList.remove(this.hiddenItemClass);
        item.removeAttribute('data-hidden-item');
      });

      if (this.loadMoreBtn) this.loadMoreBtn.classList.add('community-grid__button--hidden');
    }

    handleItemsClick(event) {
      const mediaWrapper = event.target.closest('[data-grid-wrapper]');
      if (!mediaWrapper) return;

      const videoElement = mediaWrapper.querySelector('video');
      if (videoElement) {
        this.togglePlayback(videoElement, mediaWrapper);
      }
    }

    togglePlayback(videoElement, mediaWrapper) {
      const isPaused = videoElement.paused;

      if (isPaused) {
        this.querySelectorAll('video').forEach((otherVideo) => {
          if (otherVideo !== videoElement) {
            otherVideo.pause();
            otherVideo.closest('[data-grid-wrapper]')?.classList.remove(this.playingClass);
          }
        });

        videoElement.play()
          .then(() => {
            mediaWrapper.classList.add(this.playingClass);
          })
          .catch((error) => {
            console.warn('Playback failed:', error);
          });
      } else {
        videoElement.pause();
        mediaWrapper.classList.remove(this.playingClass);
      }
    }
  }

  customElements.define('community-grid', CommunityGrid);
}

if (!customElements.get('reviews-modal')) {
  class ReviewsModal extends HTMLElement {
    constructor() {
      super();

      this.pendingSlideIndex = 0;
      this.swiperInitAttempts = 0;

      this.handleOpenClick = this.handleOpenClick.bind(this);
      this.handleModalClosed = this.handleModalClosed.bind(this);
      this.handleSlideChange = this.handleSlideChange.bind(this);
    }

    connectedCallback() {
      this.gridElement = this.querySelector('[data-grid-items]');
      this.modalElement = document.getElementById(`ReviewsModal-${this.dataset.sectionId}`);
      this.sliderElement = this.modalElement?.querySelector('[data-reviews-modal-swiper]');

      if (!this.gridElement || !this.modalElement || !this.sliderElement) return;

      this.bindSwiperEvents();

      this.gridElement.addEventListener('click', this.handleOpenClick, true);
      document.body.addEventListener('modalClosed', this.handleModalClosed);
    }

    disconnectedCallback() {
      this.gridElement?.removeEventListener('click', this.handleOpenClick, true);
      document.body.removeEventListener('modalClosed', this.handleModalClosed);

      if (this.swiperElement) {
        this.swiperElement.off('slideChange', this.handleSlideChange);
      }
    }

    handleOpenClick(event) {
      const triggerElement = event.target.closest('[data-slide-index]');
      const openerElement = triggerElement?.closest('modal-opener');

      if (!triggerElement || openerElement?.dataset.modal !== `#${this.modalElement.id}`) return;

      this.pendingSlideIndex = Number(triggerElement.dataset.slideIndex) || 0;

      requestAnimationFrame(() => this.goToSlide(this.pendingSlideIndex));
    }

    handleModalClosed() {
      window.pauseAllMedia();
    }

    handleSlideChange() {
      window.pauseAllMedia();
      requestAnimationFrame(() => this.playActiveSlideVideo());
    }

    bindSwiperEvents(attempts = 0) {
      const swiperElement = this.sliderElement.swiper;

      if (!swiperElement) {
        if (attempts > 60) return;
        requestAnimationFrame(() => this.bindSwiperEvents(attempts + 1));
        return;
      }

      this.swiperElement = swiperElement;
      this.swiperElement.off('slideChange', this.handleSlideChange);
      this.swiperElement.on('slideChange', this.handleSlideChange);
    }

    goToSlide(index, attempts = 0) {
      const swiperElement = this.sliderElement.swiper || this.swiperElement;

      if (!swiperElement) {
        if (attempts > 60) return;
        requestAnimationFrame(() => this.goToSlide(index, attempts + 1));
        return;
      }

      if (typeof swiperElement.slideToLoop === 'function') {
        swiperElement.slideToLoop(index, 0);
      } else {
        swiperElement.slideTo(index, 0);
      }

      window.pauseAllMedia();
      requestAnimationFrame(() => this.playActiveSlideVideo());
    }

    playActiveSlideVideo(attempts = 0) {
      const swiperElement = this.sliderElement.swiper || this.swiperElement;

      if (!swiperElement) {
        if (attempts > 60) return;
        requestAnimationFrame(() => this.playActiveSlideVideo(attempts + 1));
        return;
      }

      const activeSlideElement = this.sliderElement.querySelector('.swiper-slide-active');
      const videoPlayerElement = activeSlideElement?.querySelector('video-player');

      videoPlayerElement?.playVideo?.();
    }
  }

  customElements.define('reviews-modal', ReviewsModal);
}

class UpsellModal extends HTMLElement {
  constructor() {
    super();

    this.open = this.openModal.bind(this);
    this.close = this.closeModal.bind(this);
    this.trigger = this.triggerClick.bind(this);
    this.handleDialogClose = this.onDialogClose.bind(this);
  }

  get openButton() {
    return this.querySelector('[data-upsell-modal-open]');
  }

  get modalOverlay() {
    return this.querySelector('[data-modal-overlay]');
  }

  get addToBundleButton() {
    return this.querySelector('[data-add-to-bundle]');
  }

  get modalDrawer() {
    return this.querySelector('[data-modal-drawer]');
  }

  get triggerButton() {
    return document.querySelector(`[data-trigger-upsell-modal-id="${this.dataset.upsellModalId}"]`);
  }

  connectedCallback() {
    this.openButton?.addEventListener('click', this.open);
    this.modalOverlay?.addEventListener('close', this.handleDialogClose);
    this.addToBundleButton?.addEventListener('click', this.trigger);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.open);
    this.modalOverlay?.removeEventListener('close', this.handleDialogClose);
    this.addToBundleButton?.removeEventListener('click', this.trigger);
    document.removeEventListener('click', this.close);
    document.body.classList.remove('upsell-overlay');
  }

  async triggerClick() {
    if (!this.triggerButton) return;
    this.triggerButton.click();
    await this.delay(600);
    this.refreshButtonState();
  }

  openModal(event) {
    event.stopPropagation();
    this.modalOverlay?.showModal();
    requestAnimationFrame(() => {
      if (this.modalOverlay) this.modalOverlay.scrollTop = 0;
      if (this.modalDrawer) this.modalDrawer.scrollTop = 0;
    });

    document.body.classList.add('upsell-overlay');
    document.addEventListener('click', this.close);
    this.refreshButtonState();
  }

  closeModal(event) {
    if ((event.target.closest('[data-modal-drawer]') && !event.target.closest('[data-modal-close]')) || event.target.closest('[data-trigger-upsell-modal-id]')) return;
    this.closeDialog();
  }

  closeDialog() {
    if (!this.modalOverlay || !this.modalOverlay.open) return;

    this.modalOverlay.classList.add('is-closing');

    setTimeout(() => {
      this.modalOverlay.close();
      this.modalOverlay.classList.remove('is-closing');
    }, 240);
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  refreshButtonState() {
    const currentVisibleButton = this.addToBundleButton?.querySelector(`.upsell-modal-button__${this.addToBundleButton.dataset.state}`);
    if (currentVisibleButton && !currentVisibleButton.classList.contains('hidden'))
      currentVisibleButton.classList.add('hidden');
    this.addToBundleButton.dataset.state = this.triggerButton ? this.triggerButton.dataset.state : 'disabled';
    this.addToBundleButton?.querySelector(`.upsell-modal-button__${this.addToBundleButton.dataset.state}`)?.classList.remove('hidden');
  }

  onDialogClose() {
    document.body.classList.remove('upsell-overlay');
    document.removeEventListener('click', this.close);
  }
}

if (!customElements.get('upsell-modal')) {
  customElements.define('upsell-modal', UpsellModal);
}

class TechnicalSpecifications extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.toggleProductsInfo.bind(this);
    this.onVariantProductChange = this.handleVariantProductChange.bind(this);
  }

  get productButtons() {
    return this.querySelectorAll('[data-product-button]') || [];
  }

  get activeProductButton() {
    return this.querySelector('[data-product-button].is-active');
  }

  get activeInfoElement() {
    const productId = this.activeProductButton?.dataset.productButton;
    return this.querySelector(`[data-product-list="${productId}"]`);
  }

  connectedCallback() {
    this.productButtons.forEach(button => {
      button.addEventListener('click', this.toggle)
    });
    document.addEventListener('product:variant-product:change', this.onVariantProductChange);
  }

  disconnectedCallback() {
    this.productButtons.forEach(button => {
      button.removeEventListener('click', this.toggle);
    })
    document.removeEventListener('product:variant-product:change', this.onVariantProductChange);
  }

  toggleProductsInfo(event) {
    const targetButton = event.target.closest('[data-product-button]');

    if (!targetButton || targetButton.classList.contains('is-active')) return;

    if (this.activeInfoElement) this.activeInfoElement.classList.toggle('is-active');
    this.activeProductButton?.classList.toggle('is-active');

    targetButton.classList.toggle('is-active');
    if (this.activeInfoElement) this.activeInfoElement.classList.toggle('is-active');
    this.setActiveFootnote(targetButton.dataset.productButton);
  }

  handleVariantProductChange(event) {
    const productId = event?.detail?.productId ? String(event.detail.productId) : '';
    if (!productId) return;

    const targetButton = this.querySelector(`[data-product-button="${productId}"]`);
    const targetList = this.querySelector(`[data-product-list="${productId}"]`);

    if (!targetButton || !targetList) return;
    if (targetButton.classList.contains('is-active') && targetList.classList.contains('is-active')) return;

    this.productButtons.forEach((button) => button.classList.remove('is-active'));
    this.querySelectorAll('[data-product-list]').forEach((list) => list.classList.remove('is-active'));

    targetButton.classList.add('is-active');
    targetList.classList.add('is-active');
    this.setActiveFootnote(productId);
  }

  setActiveFootnote(productId) {
    this.querySelectorAll('[data-product-footnote]').forEach((footnote) => {
      footnote.classList.toggle('is-active', footnote.dataset.productFootnote === productId);
    });
  }
}

if (!customElements.get('technical-specifications')) {
  customElements.define('technical-specifications', TechnicalSpecifications);
}

function initGorgiasOverlap() {
  function checkOverlap() {
    const chat = document.querySelector('#chat-button');
    const button = document.querySelector('.product__info-wrapper .product-form__buttons');

    if (!chat || !button) return;

    const chatRect = chat.getBoundingClientRect();
    const btnRect = button.getBoundingClientRect();

    const overlap =
      chatRect.left < btnRect.right &&
      chatRect.right > btnRect.left &&
      chatRect.top < btnRect.bottom &&
      chatRect.bottom > btnRect.top;

    chat.style.visibility = overlap ? 'hidden' : 'visible';
  }

  window.addEventListener('scroll', checkOverlap, { passive: true });
  window.addEventListener('resize', checkOverlap);

  checkOverlap();
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('#chat-button')) {
    initGorgiasOverlap();
    return;
  }

  const observer = new MutationObserver(() => {
    if (document.querySelector('#chat-button')) {
      observer.disconnect();
      initGorgiasOverlap();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

function getTermsAndConditionsTargetId(isMobileViewport) {
  return isMobileViewport
    ? 'rich-text-01KRJZQZWBDMNREC26S0V26S5W'
    : 'rich-text-01KRJZNV5YZ3E3KKD4KBNW7Z2B';
}

function initTermsAndConditionsLink(isMobileViewport = false) {
  const targetBlock = document.getElementById(getTermsAndConditionsTargetId(isMobileViewport));
  if (!targetBlock) return false;

  const targetParagraph = [...targetBlock.querySelectorAll('p')].find((paragraph) =>
    paragraph.textContent.includes('Terms and Conditions')
  );

  if (!targetParagraph || targetParagraph.dataset.termsLinkProcessed === 'true') return Boolean(targetParagraph);

  const paragraphText = targetParagraph.textContent;
  if (!paragraphText.includes('Terms and Conditions')) return false;

  targetParagraph.innerHTML = paragraphText.replace(
    'Terms and Conditions',
    '<a href="/policies/terms-of-service" target="_blank">Terms and Conditions</a>'
  );
  targetParagraph.dataset.termsLinkProcessed = 'true';

  return true;
}

document.addEventListener('DOMContentLoaded', () => {
  const mobileViewportQuery = window.matchMedia('(max-width: 767px)');
  let observer;

  const startTermsObserver = () => {
    if (observer) observer.disconnect();

    if (initTermsAndConditionsLink(mobileViewportQuery.matches)) return;

    observer = new MutationObserver(() => {
      if (initTermsAndConditionsLink(mobileViewportQuery.matches)) observer.disconnect();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  mobileViewportQuery.addEventListener('change', () => {
    startTermsObserver();
  });

  startTermsObserver();
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-slider-selector]').forEach((slider) => {
    const wrapper = slider.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    wrapper.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('[data-no-swiping-buttons]')) return;

      event.stopPropagation();
      slider.swiper?.autoplay?.stop();
    });
  });
});
